import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { ClipboardList, DollarSign, Loader2, Mic, Plus, Sparkles, Square, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAutoSave } from '../hooks/useAutoSave';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import type {
  ProcessQuoteResponse,
  QuoteStatus,
  StructuredData,
  ClientDetail,
  CreateClientInput,
  QuoteScoreMissingItem,
} from '../api/types';
import {
  useUpdateQuote,
  useUpload,
  useProcessQuote,
  useUpdateQuoteStatus,
  useJobPrep,
  useCreateClient,
  useClient,
  useQuoteScore,
  usePriceCheck,
  useCreateInvoice,
} from '../api/hooks';
import { loadContractorSettings } from '../lib/contractorSettings';
import type { ContractorSettings } from '../lib/contractorSettings';
import { STATUS_TRANSITIONS, ALL_STATUSES } from '@/lib/statusTransitions';
import { DownloadPdfButton } from './DownloadPdfButton';
import { ShareQuoteButton } from './ShareQuoteButton';
import { DownloadInvoiceButton } from './DownloadInvoiceButton';
import { JobPrepView } from './JobPrepView';
import { QuoteScoreSection } from './QuoteScoreSection';
import { PriceCheckSection } from './PriceCheckSection';
import { EmailDraftButton } from './EmailDraftButton';
import { ClientAutocomplete } from './ClientAutocomplete';
import { ClientFormDrawer } from './ClientFormDrawer';
import { ClientDetailDrawer } from './ClientDetailDrawer';

interface QuoteResultProps {
  data: ProcessQuoteResponse;
}

interface EditableItem {
  id: string;
  description: string;
  quantity: string;
  unit_price: string;
}

function numericValue(s: string): number {
  const n = Number.parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

function selectOnFocus(e: React.FocusEvent<HTMLInputElement>): void {
  e.target.select();
}

let nextItemId = 0;
function createItemId(): string {
  return `item-${++nextItemId}`;
}

function mapResponseItems(
  items: Array<{ description: string; quantity: number; unit_price: number }>,
): EditableItem[] {
  return items.map(({ description, quantity, unit_price }) => ({
    id: createItemId(),
    description,
    quantity: quantity ? String(quantity) : '',
    unit_price: unit_price ? String(unit_price) : '',
  }));
}

function getInitialNotes(notes: string | undefined, defaultTerms: string): string {
  if (notes !== undefined) return notes;
  return defaultTerms || '';
}

function isItemValid(item: EditableItem): boolean {
  return (
    item.description.trim() !== '' &&
    numericValue(item.quantity) > 0 &&
    numericValue(item.unit_price) >= 0
  );
}

function getConfidenceBadgeClass(score: number): string {
  if (score >= 80) return 'bg-success/15 text-success hover:bg-success/15';
  if (score >= 50) return 'bg-warning/15 text-warning hover:bg-warning/15';
  return 'bg-destructive/15 text-destructive hover:bg-destructive/15';
}

function getVoiceButtonContent(
  isProcessing: boolean,
  isRecording: boolean,
  t: (key: string) => string,
) {
  if (isProcessing) {
    return {
      icon: <Loader2 className="mr-1 h-4 w-4 animate-spin" />,
      label: t('quote.processingVoice'),
    };
  }
  if (isRecording) {
    return {
      icon: <Square className="mr-1 h-4 w-4" />,
      label: t('quote.recordingItems'),
    };
  }
  return {
    icon: <Mic className="mr-1 h-4 w-4" />,
    label: t('quote.addByVoice'),
  };
}

interface TotalsProps {
  grandTotal: number;
  markupPercent: number;
  markupAmount: number;
  isVatRegistered: boolean;
  vatAmount: number;
  totalWithVat: number;
  vatRate: number;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function DesktopTotals({
  grandTotal,
  markupPercent,
  markupAmount,
  isVatRegistered,
  vatAmount,
  totalWithVat,
  vatRate,
  t,
}: Readonly<TotalsProps>) {
  if (!isVatRegistered && markupPercent <= 0) {
    return (
      <TableRow>
        <TableCell colSpan={3} className="text-right font-bold">
          {t('quote.grandTotal')}
        </TableCell>
        <TableCell className="text-right font-bold">€{grandTotal.toFixed(2)}</TableCell>
        <TableCell></TableCell>
      </TableRow>
    );
  }
  return (
    <>
      <TableRow>
        <TableCell colSpan={3} className="text-right">
          {t('quote.subtotal')}
        </TableCell>
        <TableCell className="text-right">€{grandTotal.toFixed(2)}</TableCell>
        <TableCell></TableCell>
      </TableRow>
      {markupPercent > 0 && (
        <TableRow>
          <TableCell colSpan={3} className="text-right">
            {t('quote.markup', { rate: markupPercent })}
          </TableCell>
          <TableCell className="text-right">€{markupAmount.toFixed(2)}</TableCell>
          <TableCell></TableCell>
        </TableRow>
      )}
      {isVatRegistered && (
        <TableRow>
          <TableCell colSpan={3} className="text-right">
            {t('quote.vatRate', { rate: vatRate })}
          </TableCell>
          <TableCell className="text-right">€{vatAmount.toFixed(2)}</TableCell>
          <TableCell></TableCell>
        </TableRow>
      )}
      <TableRow>
        <TableCell colSpan={3} className="text-right font-bold">
          {isVatRegistered ? t('quote.totalWithVat') : t('quote.grandTotal')}
        </TableCell>
        <TableCell className="text-right font-bold">€{totalWithVat.toFixed(2)}</TableCell>
        <TableCell></TableCell>
      </TableRow>
    </>
  );
}

function MobileTotals({
  grandTotal,
  markupPercent,
  markupAmount,
  isVatRegistered,
  vatAmount,
  totalWithVat,
  vatRate,
  t,
}: Readonly<TotalsProps>) {
  if (!isVatRegistered && markupPercent <= 0) {
    return (
      <div className="flex items-center justify-between rounded-lg bg-muted px-4 py-3">
        <span className="font-bold">{t('quote.grandTotal')}</span>
        <span className="font-bold text-primary">€{grandTotal.toFixed(2)}</span>
      </div>
    );
  }
  return (
    <div className="space-y-1 rounded-lg bg-muted px-4 py-3">
      <div className="flex items-center justify-between">
        <span>{t('quote.subtotal')}</span>
        <span>€{grandTotal.toFixed(2)}</span>
      </div>
      {markupPercent > 0 && (
        <div className="flex items-center justify-between">
          <span>{t('quote.markup', { rate: markupPercent })}</span>
          <span>€{markupAmount.toFixed(2)}</span>
        </div>
      )}
      {isVatRegistered && (
        <div className="flex items-center justify-between">
          <span>{t('quote.vatRate', { rate: vatRate })}</span>
          <span>€{vatAmount.toFixed(2)}</span>
        </div>
      )}
      <div className="flex items-center justify-between border-t border-border pt-1">
        <span className="font-bold">
          {isVatRegistered ? t('quote.totalWithVat') : t('quote.grandTotal')}
        </span>
        <span className="font-bold text-primary">€{totalWithVat.toFixed(2)}</span>
      </div>
    </div>
  );
}

interface QuoteActionButtonsProps {
  isDesktop: boolean;
  hasItems: boolean;
  currentStatus: QuoteStatus;
  isJobPrepPending: boolean;
  quoteId: string;
  clientEmail: string | null;
  getAccessToken: () => Promise<string>;
  pdfData: {
    structured_data: StructuredData;
    created_at: string;
    contractor: ContractorSettings;
    vatRate: number | null;
    markupPercent: number | null;
    quoteId?: string;
    paymentTermDays?: number;
    locale?: string;
    validityDays?: number;
    validUntilLabel?: string;
  };
  invoiceData: {
    structured_data: StructuredData;
    created_at: string;
    contractor: ContractorSettings;
    vatRate: number | null;
    markupPercent: number | null;
    quoteId?: string;
  };
  onShareStatusChange: (status: QuoteStatus) => Promise<void>;
  onJobPrep: () => void;
  onQuoteScore: () => void;
  isQuoteScorePending: boolean;
  onPriceCheck: () => void;
  isPriceCheckPending: boolean;
  onCreateInvoice: () => void;
  isCreateInvoicePending: boolean;
  t: (key: string) => string;
}

function QuoteActionButtons({
  isDesktop,
  hasItems,
  currentStatus,
  isJobPrepPending,
  quoteId,
  clientEmail,
  getAccessToken,
  pdfData,
  invoiceData,
  onShareStatusChange,
  onJobPrep,
  onQuoteScore,
  isQuoteScorePending,
  onPriceCheck,
  isPriceCheckPending,
  onCreateInvoice,
  isCreateInvoicePending,
  t,
}: Readonly<QuoteActionButtonsProps>) {
  const mobileClass = isDesktop ? undefined : 'flex-1 min-h-[44px]';
  const buttonSize = isDesktop ? 'sm' : ('default' as const);
  const jobPrepLabel = isJobPrepPending ? t('jobPrep.generating') : t('jobPrep.button');
  return (
    <div className={cn('flex gap-2', isDesktop ? 'flex-row flex-wrap' : 'flex-col')}>
      {/* Primary row: PDF + Share + Email side-by-side on mobile */}
      <div className={cn('flex flex-wrap gap-2', !isDesktop && 'w-full')}>
        <DownloadPdfButton data={pdfData} className={mobileClass} />
        <ShareQuoteButton
          data={pdfData}
          quoteId={quoteId}
          getAccessToken={getAccessToken}
          quoteStatus={currentStatus}
          onStatusChange={onShareStatusChange}
          className={mobileClass}
        />
        <EmailDraftButton
          quoteId={quoteId}
          clientEmail={clientEmail}
          quoteStatus={currentStatus}
          hasItems={hasItems}
          onStatusChange={onShareStatusChange}
          getAccessToken={getAccessToken}
          className={mobileClass}
        />
      </div>

      {/* Tertiary row: Invoice + Job Prep side-by-side on mobile */}
      <div className={cn('flex flex-wrap gap-2', !isDesktop && 'w-full')}>
        {currentStatus === 'accepted' && (
          <DownloadInvoiceButton data={invoiceData} className={mobileClass} />
        )}
        {currentStatus === 'accepted' && (
          <Button
            variant="outline"
            size={buttonSize}
            onClick={onCreateInvoice}
            disabled={isCreateInvoicePending}
            className={cn(!isDesktop && 'flex-1 min-h-[44px]')}
            data-testid="create-invoice-btn"
          >
            {t('invoice.createInvoice')}
          </Button>
        )}
        {hasItems && (
          <>
            <Button
              variant="outline"
              size={buttonSize}
              onClick={onJobPrep}
              disabled={isJobPrepPending}
              className={cn(!isDesktop && 'flex-1 min-h-[44px]')}
            >
              {isJobPrepPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <ClipboardList className="mr-1 h-4 w-4" />
              )}
              {jobPrepLabel}
            </Button>
            <Button
              variant="outline"
              size={buttonSize}
              onClick={onQuoteScore}
              disabled={isQuoteScorePending}
              className={cn(!isDesktop && 'flex-1 min-h-[44px]')}
              data-testid="quote-score-btn"
            >
              {isQuoteScorePending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-1 h-4 w-4" />
              )}
              {t('quoteScore.button')}
            </Button>
            <Button
              variant="outline"
              size={buttonSize}
              onClick={onPriceCheck}
              disabled={isPriceCheckPending}
              className={cn(!isDesktop && 'flex-1 min-h-[44px]')}
              data-testid="price-check-btn"
            >
              {isPriceCheckPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <DollarSign className="mr-1 h-4 w-4" />
              )}
              {t('priceCheck.button')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

interface MobileLineItemProps {
  item: EditableItem;
  index: number;
  total: number;
  isExpanded: boolean;
  isOnly: boolean;
  onToggle: () => void;
  onUpdate: (index: number, field: keyof EditableItem, value: string) => void;
  onRemove: (index: number) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function MobileLineItem({
  item,
  index,
  total,
  isExpanded,
  isOnly,
  onToggle,
  onUpdate,
  onRemove,
  t,
}: Readonly<MobileLineItemProps>) {
  return (
    <li className="list-none rounded-lg border bg-card">
      {/* Collapsed header — always visible */}
      <div className="flex items-center gap-1 pr-1">
        <button
          type="button"
          className={cn(
            'flex flex-1 items-center gap-2 px-3 py-2 text-left min-h-[44px]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
          aria-expanded={isExpanded}
          onClick={onToggle}
        >
          <div className="flex-1 min-w-0">
            <p className="truncate font-semibold text-sm">
              {item.description || t('quote.description')}
            </p>
            <p className="text-xs text-muted-foreground">
              {item.quantity || '0'} × €{numericValue(item.unit_price).toFixed(2)}
            </p>
          </div>
          <span className="font-bold text-primary text-sm shrink-0">€{total.toFixed(2)}</span>
        </button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          disabled={isOnly}
          aria-label={`${t('quote.removeItem')} ${index + 1}`}
          className="touch-target shrink-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Expanded editing area — CSS grid height animation */}
      <div
        className="grid transition-[grid-template-rows] duration-150 ease-in-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="space-y-3 px-3 pb-3">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('quote.description')}
              </label>
              <Input
                type="text"
                value={item.description}
                onChange={(e) => onUpdate(index, 'description', e.target.value)}
                aria-label={`${t('quote.description')} ${index + 1}`}
                className="mt-1 min-h-[48px] text-base"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('quote.quantity')}
                </label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={item.quantity}
                  placeholder="0"
                  onFocus={selectOnFocus}
                  onChange={(e) => onUpdate(index, 'quantity', e.target.value)}
                  aria-label={`${t('quote.quantity')} ${index + 1}`}
                  className="mt-1 min-h-[48px] text-base text-center"
                />
              </div>
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('quote.unitPrice')}
                </label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={item.unit_price}
                  placeholder="0.00"
                  onFocus={selectOnFocus}
                  onChange={(e) => onUpdate(index, 'unit_price', e.target.value)}
                  aria-label={`${t('quote.unitPrice')} ${index + 1}`}
                  className="mt-1 min-h-[48px] text-base text-center"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('quote.total')}
              </span>
              <span className="font-bold text-primary">€{total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

export function QuoteResult({ data }: Readonly<QuoteResultProps>) {
  const { t, i18n } = useTranslation();
  const { getAccessToken } = useKindeAuth();
  const tokenGetter = getAccessToken as () => Promise<string>;
  const updateQuote = useUpdateQuote(tokenGetter);
  const updateStatus = useUpdateQuoteStatus(tokenGetter);
  const voiceUpload = useUpload(tokenGetter);
  const voiceProcess = useProcessQuote(tokenGetter);
  const recorder = useVoiceRecorder();
  const jobPrep = useJobPrep(tokenGetter);
  const quoteScore = useQuoteScore(tokenGetter);
  const priceCheck = usePriceCheck(tokenGetter);
  const createInvoice = useCreateInvoice();
  const createClient = useCreateClient(tokenGetter);
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const prevVoiceError = useRef(false);

  const [currentStatus, setCurrentStatus] = useState<QuoteStatus>(data.status);
  const [showJobPrep, setShowJobPrep] = useState(false);
  const [showQuoteScore, setShowQuoteScore] = useState(false);
  const [showPriceCheck, setShowPriceCheck] = useState(false);
  const [linkedClientId, setLinkedClientId] = useState<string | null>(data.client_id ?? null);
  const linkedClient = useClient(tokenGetter, linkedClientId);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showClientDetail, setShowClientDetail] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  const handleStatusChange = useCallback(
    (newStatus: QuoteStatus) => {
      if (newStatus === currentStatus) return;
      updateStatus.mutate(
        { id: data.id, status: newStatus },
        {
          onSuccess: () => {
            setCurrentStatus(newStatus);
            toast.success(t('quoteStatus.updated'));
          },
          onError: () => {
            toast.error(t('quoteStatus.updateFailed'));
          },
        },
      );
    },
    [updateStatus, data.id, currentStatus, t],
  );

  const [contractor] = useState(loadContractorSettings);
  const isVatRegistered = contractor.vatNumber.trim() !== '';
  const markupPercent = contractor.markupPercent;

  const [clientName, setClientName] = useState(data.structured_data.client_name ?? '');
  const [notes, setNotes] = useState(() =>
    getInitialNotes(data.structured_data.notes, contractor.defaultTerms),
  );
  const [clientAddress, setClientAddress] = useState(data.structured_data.client_address ?? '');
  const [items, setItems] = useState<EditableItem[]>(
    data.structured_data.items.map(({ description, quantity, unit_price }) => ({
      id: createItemId(),
      description,
      quantity: quantity ? String(quantity) : '',
      unit_price: unit_price ? String(unit_price) : '',
    })),
  );

  const itemTotals = useMemo(
    () => items.map((item) => numericValue(item.quantity) * numericValue(item.unit_price)),
    [items],
  );

  const grandTotal = useMemo(() => itemTotals.reduce((sum, t) => sum + t, 0), [itemTotals]);

  const markupAmount = useMemo(
    () => (markupPercent > 0 ? grandTotal * (markupPercent / 100) : 0),
    [markupPercent, grandTotal],
  );

  const vatAmount = useMemo(
    () => (isVatRegistered ? (grandTotal + markupAmount) * (contractor.vatRate / 100) : 0),
    [isVatRegistered, grandTotal, markupAmount, contractor.vatRate],
  );

  const totalWithVat = useMemo(
    () => grandTotal + markupAmount + vatAmount,
    [grandTotal, markupAmount, vatAmount],
  );

  const isValid = useMemo(() => items.every(isItemValid), [items]);

  const [hasChanges, setHasChanges] = useState(false);
  const editVersionRef = useRef(0);
  const markDirty = useCallback(() => {
    editVersionRef.current++;
    setHasChanges(true);
  }, []);

  const updateItem = useCallback((index: number, field: keyof EditableItem, value: string) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    markDirty();
  }, []);

  const addItem = useCallback(() => {
    const newId = createItemId();
    setItems((prev) => [...prev, { id: newId, description: '', quantity: '', unit_price: '' }]);
    setExpandedItemId(newId);
    markDirty();
  }, []);

  const removeItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    markDirty();
  }, []);

  const handleVoiceStart = useCallback(() => {
    voiceUpload.reset();
    voiceProcess.reset();
    recorder.start();
  }, [recorder, voiceUpload, voiceProcess]);

  const handleVoiceStop = useCallback(async () => {
    const { blob, mimeType } = await recorder.stop();
    const language = i18n.language.split('-')[0];
    const onProcessed = (result: ProcessQuoteResponse) => {
      setItems((prev) => [...prev, ...mapResponseItems(result.structured_data.items)]);
      markDirty();
    };
    voiceUpload.mutate(
      { blob, mimeType },
      {
        onSuccess: (response) => {
          voiceProcess.mutate({ file_key: response.key, language }, { onSuccess: onProcessed });
        },
      },
    );
  }, [recorder, voiceUpload, voiceProcess, i18n.language]);

  useEffect(() => {
    const hasError = voiceUpload.isError || voiceProcess.isError;
    if (hasError && !prevVoiceError.current) {
      toast.error(t('error.voiceAddFailed'));
    }
    prevVoiceError.current = hasError;
  }, [voiceUpload.isError, voiceProcess.isError, t]);

  const handleShareStatusChange = useCallback(
    (newStatus: QuoteStatus) =>
      new Promise<void>((resolve, reject) => {
        updateStatus.mutate(
          { id: data.id, status: newStatus },
          {
            onSuccess: () => {
              setCurrentStatus(newStatus);
              resolve();
            },
            onError: (err) => reject(err),
          },
        );
      }),
    [updateStatus, data.id],
  );

  const handleSave = useCallback(() => {
    const saveVersion = editVersionRef.current;
    updateQuote.mutate(
      {
        id: data.id,
        structured_data: {
          client_name: clientName || null,
          items: items.map(({ description, quantity, unit_price }) => ({
            description,
            quantity: numericValue(quantity),
            unit_price: numericValue(unit_price),
          })),
          notes,
          client_address: clientAddress,
        },
      },
      {
        onSuccess: () => {
          // Only clear dirty flag if no new edits happened during save
          if (editVersionRef.current === saveVersion) {
            setHasChanges(false);
          }
          autoSaveCallbacks.current.onSuccess();
        },
        onError: () => autoSaveCallbacks.current.onError(),
      },
    );
  }, [updateQuote, data.id, clientName, items, notes, clientAddress]);

  const {
    status: autoSaveStatus,
    onSaveSuccess,
    onSaveError,
  } = useAutoSave(handleSave, hasChanges && isValid);

  const autoSaveCallbacks = useRef({ onSuccess: onSaveSuccess, onError: onSaveError });
  autoSaveCallbacks.current = { onSuccess: onSaveSuccess, onError: onSaveError };

  const handleJobPrep = useCallback(() => {
    if (jobPrep.data && showJobPrep) {
      setShowJobPrep(false);
      return;
    }
    setShowJobPrep(true);
    if (!jobPrep.data) {
      jobPrep.mutate(
        { id: data.id, language: i18n.language },
        { onError: () => toast.error(t('jobPrep.error')) },
      );
    }
  }, [jobPrep, showJobPrep, data.id, i18n.language, t]);

  const handleRegenerate = useCallback(() => {
    jobPrep.mutate(
      { id: data.id, regenerate: true, language: i18n.language },
      { onError: () => toast.error(t('jobPrep.error')) },
    );
  }, [jobPrep, data.id, i18n.language, t]);

  const handleQuoteScore = useCallback(() => {
    if (quoteScore.data && showQuoteScore) {
      setShowQuoteScore(false);
      return;
    }
    setShowQuoteScore(true);
    if (!quoteScore.data) {
      quoteScore.mutate(
        { id: data.id, language: i18n.language },
        { onError: () => toast.error(t('error.quoteScoreFailed')) },
      );
    }
  }, [quoteScore, showQuoteScore, data.id, i18n.language, t]);

  const handleRescore = useCallback(() => {
    quoteScore.mutate(
      { id: data.id, regenerate: true, language: i18n.language },
      { onError: () => toast.error(t('error.quoteScoreFailed')) },
    );
  }, [quoteScore, data.id, i18n.language, t]);

  const handleAddScoreItem = useCallback((item: QuoteScoreMissingItem) => {
    setItems((prev) => [
      ...prev,
      {
        id: createItemId(),
        description: item.description,
        quantity: String(item.estimated_quantity),
        unit_price: String(item.estimated_unit_price),
      },
    ]);
    markDirty();
  }, []);

  const handleAddAllScoreItems = useCallback((scoreItems: QuoteScoreMissingItem[]) => {
    setItems((prev) => [
      ...prev,
      ...scoreItems.map((item) => ({
        id: createItemId(),
        description: item.description,
        quantity: String(item.estimated_quantity),
        unit_price: String(item.estimated_unit_price),
      })),
    ]);
    markDirty();
  }, []);

  const handlePriceCheck = useCallback(() => {
    if (priceCheck.data && showPriceCheck) {
      setShowPriceCheck(false);
      return;
    }
    setShowPriceCheck(true);
    if (!priceCheck.data) {
      priceCheck.mutate(
        { id: data.id, language: i18n.language },
        {
          onSuccess: () => toast.success(t('priceCheck.complete')),
          onError: () => toast.error(t('priceCheck.failed')),
        },
      );
    }
  }, [priceCheck, showPriceCheck, data.id, i18n.language, t]);

  const handlePriceRecheck = useCallback(() => {
    priceCheck.mutate(
      { id: data.id, regenerate: true, language: i18n.language },
      {
        onSuccess: () => toast.success(t('priceCheck.complete')),
        onError: () => toast.error(t('priceCheck.failed')),
      },
    );
  }, [priceCheck, data.id, i18n.language, t]);

  const handleApplyPrice = useCallback((description: string, price: number) => {
    setItems((prev) =>
      prev.map((item) =>
        item.description === description ? { ...item, unit_price: String(price) } : item,
      ),
    );
    markDirty();
  }, []);

  const handleCreateInvoice = useCallback(() => {
    createInvoice.mutate(
      { quoteId: data.id },
      {
        onSuccess: () => {
          toast.success(t('invoice.created'));
        },
        onError: () => {
          toast.error(t('invoice.createFailed'));
        },
      },
    );
  }, [createInvoice, data.id, t]);

  const handleApplyAllPrices = useCallback(
    (suggestions: Array<{ description: string; price: number }>) => {
      setItems((prev) => {
        let updated = [...prev];
        for (const suggestion of suggestions) {
          updated = updated.map((item) =>
            item.description === suggestion.description
              ? { ...item, unit_price: String(suggestion.price) }
              : item,
          );
        }
        return updated;
      });
      markDirty();
      toast.success(t('priceCheck.appliedAll'));
    },
    [t],
  );

  const currentStructuredData = useMemo(
    () => ({
      client_name: clientName || null,
      items: items.map((item, index) => ({
        description: item.description,
        quantity: numericValue(item.quantity),
        unit_price: numericValue(item.unit_price),
        total: itemTotals[index],
      })),
      grand_total: grandTotal,
      confidence_score: data.structured_data.confidence_score,
      notes: notes || undefined,
      client_address: clientAddress || undefined,
    }),
    [
      clientName,
      items,
      itemTotals,
      grandTotal,
      data.structured_data.confidence_score,
      notes,
      clientAddress,
    ],
  );

  const isVoiceProcessing = voiceUpload.isPending || voiceProcess.isPending;
  const isRecording = recorder.state === 'recording';
  const voiceContent = getVoiceButtonContent(isVoiceProcessing, isRecording, t);
  const score = Math.round(data.structured_data.confidence_score);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('quote.items')}
        </span>
        <Badge className={cn('text-xs', getConfidenceBadgeClass(score))}>
          {t('quote.confidence')}: {score}%
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
        {/* Status Selector */}
        <div aria-label={t('quoteStatus.label')} className="flex flex-wrap gap-1">
          {ALL_STATUSES.map((s) => {
            const isActive = s === currentStatus;
            const isAllowed = isActive || STATUS_TRANSITIONS[currentStatus].includes(s);
            return (
              <Button
                key={s}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                className="h-8 text-xs"
                disabled={!isAllowed}
                aria-disabled={!isAllowed}
                aria-pressed={isActive}
                onClick={() => isAllowed && handleStatusChange(s)}
              >
                {t(`quoteStatus.${s}`)}
              </Button>
            );
          })}
        </div>

        <div>
          <label htmlFor="client-name" className="text-sm font-medium text-muted-foreground">
            {t('quote.client')}
          </label>
          <div className="mt-1">
            <ClientAutocomplete
              value={clientName}
              onChange={(val) => {
                setClientName(val);
                markDirty();
              }}
              onSelect={(clientId, name) => {
                setLinkedClientId(clientId);
                setClientName(name);
                updateQuote.mutate(
                  {
                    id: data.id,
                    structured_data: currentStructuredData,
                    client_id: clientId,
                  },
                  {
                    onSuccess: () => toast.success(t('clients.linked')),
                    onError: () => toast.error(t('error.saveFailed')),
                  },
                );
              }}
              onCreateNew={() => setShowClientForm(true)}
              onViewClient={() => setShowClientDetail(true)}
              linkedClientId={linkedClientId}
              getAccessToken={tokenGetter}
            />
          </div>
        </div>

        <div>
          <label htmlFor="client-address" className="text-sm font-medium text-muted-foreground">
            {t('quote.clientAddress')}
          </label>
          <textarea
            id="client-address"
            value={clientAddress}
            onChange={(e) => {
              setClientAddress(e.target.value);
              markDirty();
            }}
            placeholder={t('quote.clientAddressPlaceholder')}
            rows={2}
            maxLength={500}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm resize-none"
          />
        </div>

        {isDesktop ? (
          <div className="overflow-x-auto">
            <Table aria-label={t('quote.items')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px]">{t('quote.description')}</TableHead>
                  <TableHead className="w-[80px]">{t('quote.quantity')}</TableHead>
                  <TableHead className="w-[120px]">{t('quote.unitPrice')}</TableHead>
                  <TableHead className="w-[110px] text-right">{t('quote.total')}</TableHead>
                  <TableHead className="w-[48px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        aria-label={`${t('quote.description')} ${index + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.quantity}
                        placeholder="0"
                        onFocus={selectOnFocus}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        aria-label={`${t('quote.quantity')} ${index + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.unit_price}
                        placeholder="0.00"
                        onFocus={selectOnFocus}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        aria-label={`${t('quote.unitPrice')} ${index + 1}`}
                      />
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      €{itemTotals[index].toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        disabled={items.length === 1}
                        aria-label={`${t('quote.removeItem')} ${index + 1}`}
                        className="touch-target"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <DesktopTotals
                  grandTotal={grandTotal}
                  markupPercent={markupPercent}
                  markupAmount={markupAmount}
                  isVatRegistered={isVatRegistered}
                  vatAmount={vatAmount}
                  totalWithVat={totalWithVat}
                  vatRate={contractor.vatRate}
                  t={t}
                />
              </TableFooter>
            </Table>
          </div>
        ) : (
          <ul className="space-y-3" aria-label={t('quote.items')}>
            {items.map((item, index) => (
              <MobileLineItem
                key={item.id}
                item={item}
                index={index}
                total={itemTotals[index]}
                isExpanded={expandedItemId === item.id}
                isOnly={items.length === 1}
                onToggle={() => setExpandedItemId((prev) => (prev === item.id ? null : item.id))}
                onUpdate={updateItem}
                onRemove={removeItem}
                t={t}
              />
            ))}
            <li className="list-none">
              <MobileTotals
                grandTotal={grandTotal}
                markupPercent={markupPercent}
                markupAmount={markupAmount}
                isVatRegistered={isVatRegistered}
                vatAmount={vatAmount}
                totalWithVat={totalWithVat}
                vatRate={contractor.vatRate}
                t={t}
              />
            </li>
          </ul>
        )}

        <div className="flex gap-2">
          <Button variant="outline" onClick={addItem} className="flex-1">
            <Plus className="mr-1 h-4 w-4" />
            {t('quote.addItem')}
          </Button>
          <Button
            variant={isRecording ? 'destructive' : 'outline'}
            onClick={isRecording ? handleVoiceStop : handleVoiceStart}
            disabled={isVoiceProcessing}
            aria-label={isRecording ? t('record.stop') : t('quote.addByVoice')}
            className="flex-1"
          >
            {voiceContent.icon}
            {voiceContent.label}
          </Button>
        </div>

        <div className="mt-4">
          <label htmlFor="quote-notes" className="text-sm font-medium text-muted-foreground">
            {t('quote.notes')}
          </label>
          <textarea
            id="quote-notes"
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              markDirty();
            }}
            placeholder={t('quote.notesPlaceholder')}
            rows={3}
            maxLength={2000}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm resize-none"
          />
        </div>

        <QuoteActionButtons
          isDesktop={isDesktop}
          hasItems={items.length > 0}
          currentStatus={currentStatus}
          isJobPrepPending={jobPrep.isPending}
          quoteId={data.id}
          clientEmail={linkedClient.data?.email ?? null}
          getAccessToken={tokenGetter}
          pdfData={{
            structured_data: currentStructuredData,
            created_at: data.created_at,
            contractor,
            vatRate: isVatRegistered ? contractor.vatRate : null,
            markupPercent: markupPercent > 0 ? markupPercent : null,
            quoteId: data.id,
            paymentTermDays: contractor.paymentTermDays,
            locale: i18n.language === 'et' ? 'et-EE' : 'en-US',
            validityDays: contractor.validityDays,
            validUntilLabel: t('pdf.validUntil'),
          }}
          invoiceData={{
            structured_data: currentStructuredData,
            created_at: data.created_at,
            contractor,
            vatRate: isVatRegistered ? contractor.vatRate : null,
            markupPercent: markupPercent > 0 ? markupPercent : null,
            quoteId: data.id,
          }}
          onShareStatusChange={handleShareStatusChange}
          onJobPrep={handleJobPrep}
          onQuoteScore={handleQuoteScore}
          isQuoteScorePending={quoteScore.isPending}
          onPriceCheck={handlePriceCheck}
          isPriceCheckPending={priceCheck.isPending}
          onCreateInvoice={handleCreateInvoice}
          isCreateInvoicePending={createInvoice.isPending}
          t={t}
        />
        {showJobPrep && jobPrep.data && (
          <JobPrepView
            data={jobPrep.data}
            onRegenerate={handleRegenerate}
            isRegenerating={jobPrep.isPending}
          />
        )}
        {showQuoteScore && quoteScore.data && (
          <QuoteScoreSection
            data={quoteScore.data}
            onRescore={handleRescore}
            isRescoring={quoteScore.isPending}
            onAddItem={handleAddScoreItem}
            onAddAllItems={handleAddAllScoreItems}
          />
        )}
        {showPriceCheck && priceCheck.data && (
          <PriceCheckSection
            data={priceCheck.data}
            onRecheck={handlePriceRecheck}
            isRechecking={priceCheck.isPending}
            onApplyPrice={handleApplyPrice}
            onApplyAll={handleApplyAllPrices}
          />
        )}
        {recorder.error && (
          <p role="alert" className="text-sm text-destructive">
            {t(`error.${recorder.error}`)}
          </p>
        )}
        {autoSaveStatus === 'saving' && (
          <span className="text-xs text-muted-foreground" data-testid="auto-save-saving">
            {t('quote.autoSaving')}
          </span>
        )}
        {autoSaveStatus === 'saved' && (
          <output className="block text-xs text-green-600" data-testid="auto-save-saved">
            {t('quote.autoSaved')}
          </output>
        )}
        {autoSaveStatus === 'error' && (
          <p role="alert" className="text-xs text-destructive" data-testid="auto-save-error">
            {t('quote.autoSaveError')}
          </p>
        )}
        <ClientFormDrawer
          isOpen={showClientForm}
          onClose={() => setShowClientForm(false)}
          onSave={(clientData: CreateClientInput) => {
            createClient.mutate(clientData, {
              onSuccess: (newClient: ClientDetail) => {
                setLinkedClientId(newClient.id);
                setClientName(newClient.name);
                if (newClient.address) setClientAddress(newClient.address);
                setShowClientForm(false);
                toast.success(t('clients.saved'));
                updateQuote.mutate({
                  id: data.id,
                  structured_data: currentStructuredData,
                  client_id: newClient.id,
                });
              },
              onError: () => toast.error(t('error.saveFailed')),
            });
          }}
          isSaving={createClient.isPending}
          initialData={{ name: clientName, address: clientAddress }}
        />
        <ClientDetailDrawer
          isOpen={showClientDetail}
          onClose={() => setShowClientDetail(false)}
          clientId={linkedClientId}
          onUnlink={() => {
            setLinkedClientId(null);
            updateQuote.mutate(
              { id: data.id, structured_data: currentStructuredData, client_id: null },
              {
                onSuccess: () => {
                  setShowClientDetail(false);
                  toast.success(t('clients.unlinked'));
                },
              },
            );
          }}
          onEdit={(_client: ClientDetail) => {
            setShowClientDetail(false);
            setShowClientForm(true);
          }}
          getAccessToken={tokenGetter}
        />
      </CardContent>
    </Card>
  );
}
